/**
 * W1-TODAY-WEEK (build spec 7.7: TW-2, TW-3, TW-6) — the one week measure.
 *
 * Today stated one week three ways ("44 km" on Today, "345 min" on Train, "0 / 44 km
 * · 0 of 5 sessions" on the desktop card). This is the one function the phone list
 * (`WeekListV2`) and the desktop card both read, and it reads only what
 * `useWeeklyPlan` already produces: the adapter's stated-or-summed distance figures
 * (`weekDistanceDone`, `weekDistanceTarget`, lines 658 to 685 of the hook), each day's
 * cell (`mileage`, `mileageInUnit`) and plan text (`details`), and `weekOnTrack`.
 *
 * The Q4 rule (founder 2026-10-13, as recommended):
 *   - a DISTANCE week is one where every non-rest day states a distance; minutes on
 *     such days are ignored. It prints "6 of 40 km" (the athlete's unit);
 *   - otherwise a TIME week is one where every non-rest day states minutes and no
 *     distance. It prints "35 of 235 min";
 *   - any other week prints no total at all (kind 'none'). A mixed week, an all-rest
 *     week, a day that states neither, a day whose only distance is a rep set.
 *
 * "On plan" is the existing B330 rule, read from the adapter and nothing else: the
 * adapter's `weekOnTrack` (something done, and the distance done reaches the plan for
 * the days already passed, within the 3 percent band). It is a distance rule, so it
 * rides a distance week only; a time week never claims it, because nothing here
 * defines the same test for minutes. There is no "behind" in this file or anywhere
 * downstream (NEXT-BEHIND, Q1).
 *
 * Pure: no I/O, no clock. The caller passes the week the adapter built.
 */
import type { Day, WeeklyPlan } from '../fixtures/plan';
import {
  deriveDayMetric,
  sessionNameForType,
  statesDayDistance,
  type WeekDistanceSummary,
} from '../hooks/useWeeklyPlan';

/** A distance cell as the adapter writes it: "7 km", "12.5 mi". */
const DISTANCE_CELL = /^\d+(?:\.\d+)?\s(?:mi|km)$/;

/** A token that is only dashes and spaces says nothing. */
const PLACEHOLDER = /^[\s–—-]*$/;

/** What `WeekPlan` and `WeekPlanDesktop` call a rest day: the flag, else the legacy dash cell. */
export function isRestDay(day: Pick<Day, 'rest' | 'mileage'>): boolean {
  return day.rest ?? day.mileage === '—';
}

/** What one day's plan states, in the athlete's unit. */
export interface DayFacts {
  rest: boolean;
  /** The stated distance as the day's cell prints it ("7 km"), or null. */
  distance: string | null;
  /** The stated minutes ("40 min", a range "35 to 40 min") and the figure counted (the first), or null. */
  minutes: { text: string; value: number } | null;
}

/**
 * Read a day. A distance wins over minutes, as `deriveDayMetric` has it (minutes on a
 * day that states a distance are ignored). A day whose only distance is a rep set
 * states none (`statesDayDistance`), and since the adapter reads no minutes on a day
 * with any distance token, it states nothing: the total never counts a partial.
 */
export function dayFacts(day: Day): DayFacts {
  if (isRestDay(day)) return { rest: true, distance: null, minutes: null };
  const cell = day.mileageInUnit ?? day.mileage;
  // Fixture days carry no plan text; their cell is all they state.
  const text = day.details && day.details.trim() ? day.details : cell;
  if (statesDayDistance(text) && DISTANCE_CELL.test(cell)) {
    return { rest: false, distance: cell, minutes: null };
  }
  const metric = deriveDayMetric(text);
  if (metric.minutes > 0) {
    return { rest: false, distance: null, minutes: { text: metric.display, value: metric.minutes } };
  }
  return { rest: false, distance: null, minutes: null };
}

/**
 * The session's name from what a `Day` carries. THE one name function for a day: the
 * week row, the day panel title, the effort sheet's sub-line and Tomorrow all read it
 * (W1-TODAY-FIX1, F4, B397), so one day never reads two ways.
 *
 * The adapter attaches the plan day's own workout type (`DayWithType`), and the name
 * is `sessionNameForType` of it, the words the hero's title builder uses: a recovery
 * run is "Recovery run" everywhere, and "Cross-training" is never cut to "Cross-tr".
 *
 * A day built without a type (a fixture, a hand-built day) falls back to what it
 * carries: the classified label ("Easy", "Long", "Tempo", "Hard") and a truncated raw
 * type for anything else. "Hard" is interval, hill or repeat work; a hill word in the
 * plan text names it a hill workout, otherwise it is intervals.
 */
export function sessionName(day: Pick<Day, 'label' | 'rest' | 'mileage' | 'details'> & { workoutType?: string }): string {
  if (isRestDay(day)) return 'Rest';
  const full = sessionNameForType(day.workoutType);
  if (full) return full;
  switch (day.label) {
    case 'Easy':
      return 'Easy run';
    case 'Long':
      return 'Long run';
    case 'Tempo':
      return 'Tempo run';
    case 'Hard':
      return /\bhill/i.test(day.details ?? '') ? 'Hill workout' : 'Intervals';
    default:
      return PLACEHOLDER.test(day.label) ? 'Run' : day.label;
  }
}

/**
 * The session with its measure: "Easy run, 7 km" in a week row, "Tempo run · 9 km" in
 * the day panel's title (string 38 and 44). A rest day is "Rest", never a dash
 * placeholder (B364). A day that states neither a distance nor minutes is its name alone.
 */
export function daySessionLine(day: Day, separator: ', ' | ' · ' = ', '): string {
  const facts = dayFacts(day);
  if (facts.rest) return 'Rest';
  const measure = facts.distance ?? facts.minutes?.text ?? null;
  return measure ? `${sessionName(day)}${separator}${measure}` : sessionName(day);
}

/**
 * B375 — is this day after today in its week? Derived from position: the day flagged
 * `today` by the adapter is the pivot. A week with no day flagged today (so no way to
 * say where today falls) has no future days, which leaves every control as it was.
 */
export function isFutureDay(days: readonly Day[], dayId: string | undefined): boolean {
  if (!dayId) return false;
  const todayIndex = days.findIndex((d) => d.today);
  if (todayIndex < 0) return false;
  const index = days.findIndex((d) => d.id === dayId);
  return index > todayIndex;
}

/** The week as one measure. `kind: 'none'` prints no total. */
export type WeekMeasure =
  | { kind: 'none' }
  | {
      kind: 'distance';
      done: number;
      planned: number;
      unit: 'mi' | 'km';
      /** "6 of 40 km" */
      text: string;
      /** The B330 rule, from the adapter's `weekOnTrack`. */
      onPlan: boolean;
    }
  | {
      kind: 'time';
      done: number;
      planned: number;
      unit: 'min';
      /** "35 of 235 min" */
      text: string;
      /** A time week never claims "On plan" (see the file header). */
      onPlan: false;
    };

export function weekMeasure(plan: WeeklyPlan & Partial<WeekDistanceSummary>): WeekMeasure {
  const facts = plan.days.map(dayFacts);
  const training = facts.filter((f) => !f.rest);
  if (training.length === 0) return { kind: 'none' };

  if (training.every((f) => f.distance !== null)) {
    // The athlete's own unit, the way the legacy desktop header picks it: kilometre
    // figures only when the adapter produced them, else the miles every athlete has.
    const km =
      plan.distanceUnit === 'km' &&
      typeof plan.weekDistanceDone === 'number' &&
      typeof plan.weekDistanceTarget === 'number';
    const unit: 'mi' | 'km' = km ? 'km' : 'mi';
    const done = km ? (plan.weekDistanceDone as number) : plan.weekMileageDone;
    const planned = km ? (plan.weekDistanceTarget as number) : plan.weekMileageTarget;
    if (!(planned > 0)) return { kind: 'none' };
    return {
      kind: 'distance',
      done,
      planned,
      unit,
      text: `${done} of ${planned} ${unit}`,
      onPlan: plan.weekOnTrack === true,
    };
  }

  if (training.every((f) => f.minutes !== null)) {
    let planned = 0;
    let done = 0;
    plan.days.forEach((day, i) => {
      const minutes = facts[i].minutes;
      if (facts[i].rest || !minutes) return;
      planned += minutes.value;
      if (day.completionStatus === 'completed') done += minutes.value;
    });
    if (!(planned > 0)) return { kind: 'none' };
    return { kind: 'time', done, planned, unit: 'min', text: `${done} of ${planned} min`, onPlan: false };
  }

  return { kind: 'none' };
}
